import { useNavigate, useParams } from 'react-router';
import { Button } from 'flowbite-react';
import { useEffect } from 'react';
import IndicatorPageLoader from 'navigation/loaders/Indicator';
import BackArrowButtonIcon from 'assets/svg/BackArrowButton';
import { useGetDisputeStatusQuery } from 'api/queries/escrow/get-dispute-status';
import { DisputeStatusEnum, TERMINAL_DISPUTE_STATUSES } from 'utils/enums';
import ArbitrationFundingSection from './components/ArbitrationFundingSection';
import DecisionSection from './components/DecisionSection';
import DisputeChatPanel from './components/DisputeChatPanel';
import DisputeInfo from './components/DisputeInfo';
import DisputeStatusBadge from './components/DisputeStatusBadge';
import EvidenceSection from './components/EvidenceSection';
import TriageSection from './components/TriageSection';

const DECISION_STATUSES = ['arbitration_decision_issued', 'closed', 'deemed_loss_resolved'] as const;

export default function DisputeDetailsPage() {
  const navigate = useNavigate();
  const goBack = () => navigate(-1);
  const { disputeId = '' } = useParams<{ disputeId: string }>();

  const isTerminal = (status: string) => TERMINAL_DISPUTE_STATUSES.includes(status as DisputeStatusEnum);

  const {
    data: dispute,
    isLoading,
    isError,
    error,
  } = useGetDisputeStatusQuery(disputeId, {
    enabled: !!disputeId,
    polling: true,
  });

  useEffect(() => {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 403) {
      navigate('/404', { replace: true });
    }
  }, [error, navigate]);

  if (isLoading) {
    return <IndicatorPageLoader fullPage />;
  }

  if (isError || !dispute) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] p-4">
        <h2 className="text-xl font-bold text-brand-black mb-2">Dispute Not Found</h2>
        <p className="text-sm text-gray-500 mb-4">This dispute does not exist or you do not have access to it.</p>
        <Button pill color="light" className="font-semibold border-0 bg-brand-purple-light" onClick={goBack}>
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col p-4 md:p-10">
      {/* page header — back button, title, status badge */}
      <div className="flex gap-2 lg:gap-4 items-center">
        <Button
          pill
          color="light"
          className="font-semibold border-0 bg-brand-purple-light hidden md:block"
          onClick={goBack}
        >
          Back
        </Button>
        <button className="hover:shadow-md md:hidden" onClick={goBack}>
          <BackArrowButtonIcon size={26} />
        </button>
        <h2 className="whitespace-pre-wrap text-xl md:text-[32px] leading-normal font-black text-brand-black">
          Dispute
        </h2>
        <DisputeStatusBadge status={dispute.status} />
      </div>

      <div className="h-4 md:h-8" />

      {/* two-column layout: dispute sections on the left, chat panel on the right */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6 lg:gap-8 items-start">
        {/* left column — all dispute sections */}
        <div className="flex flex-col gap-6 md:gap-10">
          <DisputeInfo dispute={dispute} />

          {(dispute.status === 'open_triage' || dispute.status === 'triage_resolved') && (
            <TriageSection dispute={dispute} />
          )}

          {!isTerminal(dispute.status) && <EvidenceSection dispute={dispute} />}

          {dispute.status === 'pending_arbitration_prefunding' && <ArbitrationFundingSection dispute={dispute} />}

          {(DECISION_STATUSES as ReadonlyArray<string>).includes(dispute.status) && (
            <DecisionSection dispute={dispute} />
          )}
        </div>

        {/* right column — trade chat panel, sticky on large screens */}
        <div className="lg:sticky lg:top-6">
          <DisputeChatPanel tradeProductId={dispute.tradeEscrow?.tradeProductId} />
        </div>
      </div>
    </div>
  );
}
